import { MetricsEnum } from '@/pages/home/DashboardPage';
import { queryKeys } from '@/queryKeys';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { AgentEstimatesStats } from '@/types/stats/AgentsEstimateStatsColumnDef';
import { DistributorEstimateStats } from '@/types/stats/DistributorEstimateStatsColumnDef';
import { ReceiverEstimateStats } from '@/types/stats/ReceiverEstimateStatsColumnDef';
import { ReservationSummary } from '@/types/stats/ReservationSummaryColumnDef';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';

const fetchStats = async (type: MetricsEnum) => {
  const response = await axios.get(`/stats/${type}`);
  return response.data;
};

export const useStatsCount = (type: MetricsEnum) => {
  return useQuery({
    queryKey: queryKeys.stats.count(type),
    queryFn: () => fetchStats(type),
  });
};

type DistributorsEstimateStatsResponse = {
  agencies: DistributorEstimateStats[];
  total: number;
};

type EstimateStatsParams = {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: {
    agencyId?: string;
    month?: string; // Expected format: 'YYYY-MM'
  };
};

const fetchDistributorsEstimateStats = async (params?: EstimateStatsParams) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<DistributorsEstimateStatsResponse>(
    `/stats/distributors/reservations`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetDistributorsEstimateStats = (
  params?: EstimateStatsParams,
) => {
  return useQuery({
    queryKey: queryKeys.stats.distributorsEstimates(params),
    queryFn: () => fetchDistributorsEstimateStats(params),
    placeholderData: keepPreviousData,
  });
};

type AgentsEstimateStatsResponse = {
  agents: AgentEstimatesStats[];
  total: number;
};

const fetchAgentsEstimateStats = async (params?: EstimateStatsParams) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([key, value]) => key !== 'agencyId' && value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<AgentsEstimateStatsResponse>(
    `/stats/distributor/agents`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetAgentsEstimateStats = (params?: EstimateStatsParams) => {
  return useQuery({
    queryKey: queryKeys.stats.distributorAgents(params),
    queryFn: () => fetchAgentsEstimateStats(params),
    placeholderData: keepPreviousData,
  });
};

type ReceiverEstimateStatsResponse = {
  receivers: ReceiverEstimateStats[];
  total: number;
};

const fetchReceiverEstimateStats = async (params?: EstimateStatsParams) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<ReceiverEstimateStatsResponse>(
    `/stats/receivers/reservations`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetReceiverEstimateStats = (params?: EstimateStatsParams) => {
  return useQuery({
    queryKey: queryKeys.stats.receiversEstimates(params),
    queryFn: () => fetchReceiverEstimateStats(params),
    placeholderData: keepPreviousData,
  });
};

type ReservationSummaryResponse = {
  estimates: ReservationSummary[];
  total: number;
  agencies: { id: string; name: string }[];
  agents: { id: string; firstname: string; lastname: string }[];
  clients: {
    id: string;
    agencyName?: string | null;
    mainContact: { firstName: string; lastName: string };
  }[];
};

type ReservationSummaryParams = Omit<EstimateStatsParams, 'filters'> & {
  filters?: {
    agencyId?: string;
    agentId?: string;
    clientId?: string;
    confirmationMonth?: string; // Expected format: 'YYYY-MM'
    reservationRequestMonth?: string; // Expected format: 'YYYY-MM'
    creationMonth?: string; // Expected format: 'YYYY-MM'
  };
};

const fetchReservationsSummary = async (
  params?: ReservationSummaryParams,
  status: 'reserved' | 'requests' = 'reserved',
) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<ReservationSummaryResponse>(
    `/stats/reservations/${status}`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetConfirmedReservationsSummary = <
  TData = ReservationSummaryResponse,
>(
  params?: ReservationSummaryParams,
  select?: (data: ReservationSummaryResponse) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.stats.reservationsReserved(params),
    queryFn: () => fetchReservationsSummary(params, 'reserved'),
    placeholderData: keepPreviousData,
    select,
  });
};

export const useGetRequestsReservationsSummary = <
  TData = ReservationSummaryResponse,
>(
  params?: ReservationSummaryParams,
  select?: (data: ReservationSummaryResponse) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.stats.reservationsRequests(params),
    queryFn: () => fetchReservationsSummary(params, 'requests'),
    placeholderData: keepPreviousData,
    select,
  });
};

type AdminReservationsSummaryResponse = ReservationSummaryResponse & {
  receivers: {
    id: string;
    name: string;
  }[];
};

type AdminReservationsSummaryParams = ReservationSummaryParams & {
  filters: ReservationSummaryParams['filters'] & {
    companyId?: string;
  };
  status: EstimateStatus;
};

const getAllReservationsSummary = async (
  params?: AdminReservationsSummaryParams,
) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<AdminReservationsSummaryResponse>(
    `/stats/admin/reservations`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetAllReservationsSummary = <
  TData = AdminReservationsSummaryResponse,
>(
  params?: AdminReservationsSummaryParams,
  select?: (data: AdminReservationsSummaryResponse) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.stats.adminReservations(params),
    queryFn: () => getAllReservationsSummary(params),
    placeholderData: keepPreviousData,
    select,
  });
};

type DistributorReservationsSummaryResponse = Omit<
  AdminReservationsSummaryResponse,
  'agencies'
>;

type DistributorReservationsSummaryParams = AdminReservationsSummaryParams & {
  filters: Omit<AdminReservationsSummaryParams['filters'], 'agencyId'>;
};

const getDistributorReservationsWithStats = async (
  params?: DistributorReservationsSummaryParams,
) => {
  const filtersStr = Object.entries(params?.filters ?? {})
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const response = await axios.get<DistributorReservationsSummaryResponse>(
    `/stats/distributor/reservations`,
    { params: { ...params, filters: filtersStr } },
  );
  return response.data;
};

export const useGetDistributorReservationsWithStats = <
  TData = DistributorReservationsSummaryResponse,
>(
  params?: DistributorReservationsSummaryParams,
  select?: (data: DistributorReservationsSummaryResponse) => TData,
) => {
  return useQuery({
    queryKey: queryKeys.stats.distributorReservations(params),
    queryFn: () => getDistributorReservationsWithStats(params),
    placeholderData: keepPreviousData,
    select,
  });
};
